/**
 * A day mark in a My week day header (3.2 stage 2), and the popper that says
 * why. One control with two triggers (MY-WEEK-INSPECT-PROMPT.md):
 *
 * - **Outside a drag** it opens on hover or focus of the mark, like a tooltip.
 * - **During a drag** a browser shows no tooltips, so the page opens it — after
 *   the cursor has rested on the day for a moment — through `dragOpen`, and
 *   closes it when the cursor moves to another day, leaves the columns, or the
 *   card is let go.
 *
 * The popper ignores the pointer, so the column under it keeps every drag event
 * it would otherwise take; it flips to stay on screen when the header is
 * scrolled out of view. A day that works opens it only when there is a note
 * worth reading — the green check already says the rest.
 */
import { useState } from "react";
import { Box, Paper, Popper, Stack, Typography } from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import WarningAmber from "@mui/icons-material/WarningAmber";
import type { DayMark as DayMarkModel } from "../../engine/dayMarks";

export function DayMark({ mark, dragOpen }: { mark: DayMarkModel; dragOpen: boolean }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [hovered, setHovered] = useState(false);
  if (mark.kind === "none") return null;
  const caution = mark.kind === "caution";
  const lines = caution ? mark.reasons : mark.notes;
  const open = (dragOpen || hovered) && lines.length > 0 && !!anchor;
  const label = caution ? `Caution: ${mark.reasons.join("; ")}` : "This day works";
  return (
    <>
      <Box
        ref={setAnchor}
        component="span"
        tabIndex={0}
        role="img"
        aria-label={label}
        data-day-mark={mark.kind}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        // A click on the mark is not a click on the column: it does not open the day.
        onClick={(e) => e.stopPropagation()}
        sx={{ display: "inline-flex", alignItems: "center", color: caution ? "warning.main" : "success.main", outline: "none" }}
      >
        {caution ? <WarningAmber sx={{ fontSize: 18 }} /> : <CheckCircleOutline sx={{ fontSize: 18 }} />}
      </Box>
      <Popper
        open={open}
        anchorEl={anchor}
        placement="bottom"
        modifiers={[
          { name: "flip", options: { fallbackPlacements: ["top", "right", "left"] } },
          { name: "preventOverflow", options: { padding: 8 } },
          { name: "offset", options: { offset: [0, 4] } },
        ]}
        sx={{ zIndex: (theme) => theme.zIndex.tooltip, pointerEvents: "none" }}
      >
        <Paper elevation={6} data-testid="day-popper" sx={{ px: 1.25, py: 0.75, maxWidth: 260 }}>
          <Stack spacing={0.25}>
            {lines.map((line) => (
              <Typography key={line} variant="body2">
                {line}
              </Typography>
            ))}
          </Stack>
        </Paper>
      </Popper>
    </>
  );
}
